import { useTranslation } from 'react-i18next'
import { Button } from '@/shared/components/ui/button'
import { useTour } from './use-tour'

interface TourTooltipProps {
  placement: 'top' | 'bottom' | 'left' | 'right'
}

export function TourTooltip({ placement }: TourTooltipProps) {
  const { t } = useTranslation()
  const { activeChapter, currentStep, currentStepIndex, totalSteps, nextStep, prevStep, skipChapter, nextGroupChapter } =
    useTour()

  if (!currentStep || currentStepIndex === null || !activeChapter) return null

  const isLast = currentStepIndex === totalSteps - 1
  const lastLabel = nextGroupChapter
    ? t('tour.common.nextChapter', 'Next: {{chapter}}', { chapter: nextGroupChapter })
    : t('tour.common.done', 'Done')

  return (
    <div
      className={`
        relative w-[320px] rounded-[12px] border-[0.5px] border-[var(--gold-border)]
        bg-[var(--gold-bg)] p-5 shadow-[0_12px_40px_rgba(26,26,24,0.10)]
      `}
    >
      {/* Arrow */}
      <div
        className={`
          absolute h-2.5 w-2.5 rotate-45 border-[0.5px] border-[var(--gold-border)] bg-[var(--gold-bg)]
          ${placement === 'bottom' ? '-top-[6px] left-8 border-b-0 border-r-0' : ''}
          ${placement === 'top' ? '-bottom-[6px] left-8 border-t-0 border-l-0' : ''}
          ${placement === 'left' ? '-right-[6px] top-6 border-l-0 border-b-0' : ''}
          ${placement === 'right' ? '-left-[6px] top-6 border-r-0 border-t-0' : ''}
        `}
      />

      {/* Inner white content area */}
      <div className="rounded-[8px] bg-white p-4 mb-3.5">
        <p className="mb-1.5 text-[15px] font-semibold text-[var(--ink)]">
          {t(`tour.step.${activeChapter.id}.${currentStep.id}.title`, currentStep.title)}
        </p>
        <p className="text-[13.5px] leading-[1.65] text-[var(--ink)]">
          {t(`tour.step.${activeChapter.id}.${currentStep.id}.body`, currentStep.body)}
        </p>
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between">
        <span className="text-[11px] tracking-[0.06em] text-[var(--gold-ink)]">
          {t('tour.tooltip.footer', '{{chapter}} · {{current}} of {{total}}', {
            chapter: t(`tour.chapter.${activeChapter.id}.title`, activeChapter.title),
            current: currentStepIndex + 1,
            total: totalSteps,
          })}
        </span>
        <div className="flex items-center gap-1.5">
          <Button variant="ghost" size="xs" onClick={skipChapter}>
            {t('tour.common.skip', 'Skip')}
          </Button>
          {currentStepIndex > 0 && (
            <Button variant="outline" size="xs" onClick={prevStep}>
              {t('tour.common.back', 'Back')}
            </Button>
          )}
          <Button size="xs" onClick={nextStep} data-tour-action="next">
            {isLast ? lastLabel : t('tour.common.next', 'Next')}
          </Button>
        </div>
      </div>
    </div>
  )
}
